import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { useCurrentUnit } from './useCurrentUnit.js'

// Drives the Weekly Insights screen.
//
// Fetches the last 14 days of: daily_sales, waste_logs, receive_logs,
// schedule_assignments, plus recent waste rollups by item. Splits the
// data into "this week" (last 7 days) and "prior week" (the 7 days
// before that) so the AI can frame the brief as week-over-week.
//
// Computes a compact snapshot the screen passes straight into
// generate-weekly-insights — no shaping inside the screen.
export function useWeeklyInsightsData() {
  const [snapshot, setSnapshot] = useState(null)
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState(null)

  // Lifetime "has this account EVER recorded a sale" signal. The
  // trailing-14-day snapshot can be all-zero for an established account
  // (stale CSV import, seasonal closure, slow fortnight) — that is NOT a
  // brand-new account and must NOT get the empty nudge. This cheap
  // head:true count gates the new-account experience on lifetime history,
  // not the current window. Starts null (unknown) and fails safe: any
  // consumer treats null/error as "has data" so the nudge never fires by
  // accident on an established account.
  const [everHadSales, setEverHadSales] = useState(null)

  // Multi-unit scope — null for single-unit (queries byte-identical).
  const { currentRestaurantId } = useCurrentUnit()

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)

    // Unit filter applied only when grouped; single-unit (null) untouched.
    const scope = (q) => (currentRestaurantId ? q.eq('restaurant_id', currentRestaurantId) : q)

    // Window math, browser-local TZ. Two consecutive 7-day buckets.
    const today = new Date()
    today.setHours(23, 59, 59, 999)
    const todayStr = today.toLocaleDateString('en-CA')
    const day = (offset) => {
      const d = new Date(today)
      d.setDate(d.getDate() - offset)
      return d
    }
    const ymd = (d) => d.toLocaleDateString('en-CA')

    const thisStart  = day(6)   // 6 days ago through today = 7 days
    const priorStart = day(13)
    const priorEnd   = day(7)
    const thisStartStr  = ymd(thisStart)
    const priorStartStr = ymd(priorStart)
    const priorEndStr   = ymd(priorEnd)
    const sinceTs = priorStart.toISOString().slice(0, 19) + 'Z'

    try {
      const [salesRes, wasteRes, recvRes, schedRes, lifetimeRes] = await Promise.all([
        scope(supabase
          .from('daily_sales')
          .select('sale_date, gross_sales, net_sales, transaction_count')
          .gte('sale_date', priorStartStr)
          .lte('sale_date', todayStr)
          .order('sale_date')),
        scope(supabase
          .from('waste_logs')
          .select('item_name, quantity, unit, total_cost, reason, logged_at')
          .gte('logged_at', sinceTs)),
        scope(supabase
          .from('receive_logs')
          .select('item_name, quantity, unit, total_cost, supplier_name, logged_at')
          .gte('logged_at', sinceTs)),
        scope(supabase
          .from('schedule_assignments')
          .select(`
            id, shift_date, start_time, end_time, status,
            staff:staff(id, name, hourly_wage)
          `)
          .gte('shift_date', priorStartStr)
          .lte('shift_date', todayStr)),
        // Lifetime sales-existence probe. head:true + count:'exact'
        // returns only a count (no rows) so it stays cheap. Same scope()
        // pattern as the windowed queries above.
        scope(supabase
          .from('daily_sales')
          .select('id', { count: 'exact', head: true })),
      ])

      if (salesRes.error) throw salesRes.error
      if (wasteRes.error) throw wasteRes.error
      if (recvRes.error)  throw recvRes.error
      if (schedRes.error) throw schedRes.error

      // Lifetime signal. Fail safe: a failed/null count is treated as
      // "has data" (true) downstream, so a probe error never triggers the
      // brand-new-account nudge on an established account.
      if (lifetimeRes.error) {
        // eslint-disable-next-line no-console
        console.warn('[useWeeklyInsightsData] lifetime count failed', lifetimeRes.error)
        setEverHadSales(true)
      } else {
        setEverHadSales((lifetimeRes.count || 0) > 0)
      }

      // Helper: bucket rows into this/prior weeks by date string.
      function inThis(dateStr)  { return dateStr >= thisStartStr  }
      function inPrior(dateStr) { return dateStr >= priorStartStr && dateStr <= priorEndStr }

      // Sales aggregates per bucket
      function aggSales(rows) {
        let net = 0, gross = 0, tx = 0, days = 0
        for (const r of rows) {
          net   += Number(r.net_sales   || 0)
          gross += Number(r.gross_sales || 0)
          tx    += Number(r.transaction_count || 0)
          days++
        }
        return { net, gross, transaction_count: tx, days_with_data: days }
      }

      // Waste aggregates per bucket. Also collect top-3 wasted items.
      function aggWaste(rows) {
        let total = 0
        const byItem = new Map()
        for (const r of rows) {
          const c = Number(r.total_cost || 0)
          total += c
          const k = r.item_name || 'Unknown'
          byItem.set(k, (byItem.get(k) || 0) + c)
        }
        const top = [...byItem.entries()]
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([item_name, cost]) => ({ item_name, cost: Number(cost.toFixed(2)) }))
        return { total: Number(total.toFixed(2)), top_items: top }
      }

      // Receive aggregates
      function aggReceive(rows) {
        let total = 0
        for (const r of rows) total += Number(r.total_cost || 0)
        return { total: Number(total.toFixed(2)) }
      }

      // Labor aggregates from schedule_assignments (only counted statuses).
      function aggLabor(rows) {
        let cost = 0, hours = 0, shifts = 0
        for (const a of rows) {
          if (a.status !== 'scheduled' && a.status !== 'completed') continue
          const start = a.start_time
          const end   = a.end_time
          if (!start || !end) continue
          const [sh, sm] = start.split(':').map(Number)
          const [eh, em] = end.split(':').map(Number)
          let h = (eh + em / 60) - (sh + sm / 60)
          if (h < 0) h += 24
          const wage = Number(a.staff?.hourly_wage || 0)
          cost   += h * wage
          hours  += h
          shifts++
        }
        return {
          cost: Number(cost.toFixed(2)),
          hours: Number(hours.toFixed(1)),
          shifts,
        }
      }

      // Bucket each table
      const salesThis = aggSales((salesRes.data || []).filter((r) => inThis(r.sale_date)))
      const salesPrior = aggSales((salesRes.data || []).filter((r) => inPrior(r.sale_date)))

      const wasteThis = aggWaste(
        (wasteRes.data || []).filter((r) => r.logged_at && r.logged_at.slice(0, 10) >= thisStartStr)
      )
      const wastePrior = aggWaste(
        (wasteRes.data || []).filter((r) => {
          const d = (r.logged_at || '').slice(0, 10)
          return d >= priorStartStr && d <= priorEndStr
        })
      )

      const recvThis = aggReceive(
        (recvRes.data || []).filter((r) => r.logged_at && r.logged_at.slice(0, 10) >= thisStartStr)
      )
      const recvPrior = aggReceive(
        (recvRes.data || []).filter((r) => {
          const d = (r.logged_at || '').slice(0, 10)
          return d >= priorStartStr && d <= priorEndStr
        })
      )

      const laborThis  = aggLabor((schedRes.data || []).filter((a) => inThis(a.shift_date)))
      const laborPrior = aggLabor((schedRes.data || []).filter((a) => inPrior(a.shift_date)))

      // Derived percentages
      function pct(num, denom) {
        return denom > 0 ? Number(((num / denom) * 100).toFixed(2)) : null
      }

      const snap = {
        windows: {
          this_week_start:  thisStartStr,
          this_week_end:    todayStr,
          prior_week_start: priorStartStr,
          prior_week_end:   priorEndStr,
        },
        sales: {
          this_week:  salesThis,
          prior_week: salesPrior,
          delta_net:  Number((salesThis.net   - salesPrior.net  ).toFixed(2)),
          delta_gross: Number((salesThis.gross - salesPrior.gross).toFixed(2)),
          delta_tx:   salesThis.transaction_count - salesPrior.transaction_count,
        },
        waste: {
          this_week:  wasteThis,
          prior_week: wastePrior,
          delta_cost: Number((wasteThis.total - wastePrior.total).toFixed(2)),
          waste_pct_of_sales_this:  pct(wasteThis.total,  salesThis.net),
          waste_pct_of_sales_prior: pct(wastePrior.total, salesPrior.net),
        },
        receivings: {
          this_week:  recvThis,
          prior_week: recvPrior,
          delta_cost: Number((recvThis.total - recvPrior.total).toFixed(2)),
        },
        labor: {
          this_week:  laborThis,
          prior_week: laborPrior,
          delta_cost: Number((laborThis.cost - laborPrior.cost).toFixed(2)),
          labor_pct_of_sales_this:  pct(laborThis.cost,  salesThis.net),
          labor_pct_of_sales_prior: pct(laborPrior.cost, salesPrior.net),
        },
        prime_cost: {
          this_week_pct: pct(
            recvThis.total + laborThis.cost,
            salesThis.net,
          ),
          prior_week_pct: pct(
            recvPrior.total + laborPrior.cost,
            salesPrior.net,
          ),
        },
      }

      setSnapshot(snap)
    } catch (e) {
      setError(e)
      // eslint-disable-next-line no-console
      console.error('[useWeeklyInsightsData]', e)
    } finally {
      setLoading(false)
    }
  }, [currentRestaurantId])

  useEffect(() => { fetchAll() }, [fetchAll])

  return {
    snapshot,
    loading,
    error,
    everHadSales,
    refresh: fetchAll,
  }
}
